<template>
    <div class="upgrade-container">
        <b-modal ref="upgradeModal" size="lg" :title="modalTitle" header-bg-variant="primary" header-text-variant="light" body-bg-variant="primary" body-text-variant="light" no-close-on-backdrop no-close-on-esc>
          
          <div v-if="isTrialUser && !isTrialExpired">
            <!-- Trial Period Active -->
            <h5>🎉 Welcome to Your CPDme Trial!</h5>
            <p style="color: white;">You're currently on a <strong>3-month trial</strong> with full access to CPDme.</p>
            
            <!-- Entry Usage Display -->
            <div class="usage-container">
              <div class="usage-label">TRIAL ENTRIES USED</div>
              <div class="usage-display">{{ trialStatus.trialEntriesUsed }}/{{ trialStatus.trialEntriesLimit }}</div>
              <div v-if="entriesRemaining > 0" class="usage-remaining">{{ entriesRemaining }} {{ entriesRemaining === 1 ? 'entry' : 'entries' }} remaining</div>
              <div v-else class="usage-warning">⚠️ Entry limit reached! Upgrade to continue adding entries.</div>
            </div>

            <!-- Trial Countdown Display -->
            <div class="countdown-container">
              <div class="countdown-label">⏰ TRIAL TIME REMAINING</div>
              <div class="countdown-display">{{ trialCountdownText }}</div>
              <div class="countdown-info">Trial ends {{ trialEndDate }}</div>
            </div>

            <p style="color: white;">After your trial ends, your account will be <strong>locked for 3 months</strong> before permanent deletion.</p>
          </div>

          <div v-else>
            <!-- Account Locked (Post-Trial or Legacy Free) -->
            <h5>⚠️ Trial Period Ended - Account Locked</h5>
            <p style="color: white;"><strong>Your trial has ended and your account is now locked.</strong></p>
            <p style="color: white;">Your account will be permanently deleted on <strong>{{ deletionDateFormatted }}</strong>.</p>
            
            <!-- Entry Count Warning -->
            <div class="entry-warning" v-if="entryCount > 0">
              <p style="color: #ffc107; font-weight: bold; margin: 10px 0;">
                ⚠️ You have {{ entryCount }} {{ entryCount === 1 ? 'entry' : 'entries' }} that will be permanently lost if you don't upgrade!
              </p>
            </div>

            <!-- Deletion Countdown Display -->
            <div class="countdown-container">
              <div class="countdown-label">⏰ TIME UNTIL DELETION</div>
              <div class="countdown-display">{{ deletionCountdownText }}</div>
              <div class="countdown-info">Account will be deleted on {{ deletionDateFormatted }}</div>
              <div class="countdown-warning">UPGRADE NOW TO SAVE YOUR ACCOUNT!</div>
            </div>
          </div>

          <p style="color: white;">Upgrade your account to Premium membership to continue using CPDme.</p>
          <p style="color: white;"> Premium membership gives you full, unlimited access to CPDme with fantastic features
          including the ability to record reflections and generate your bespoke portfolio. </p>

          <div slot="modal-footer" class="upgrade-footer">
              <b-row class="upgrade-buttons">
                  <b-col cols="6" >
                      <b-button class="upgrade-btn" variant="info" size="md" @click="upgradeMonthly">
                        <div class="upgrade-btn-content">
                          <div class="upgrade-btn-title">Monthly membership</div>
                          <div class="upgrade-btn-price">£2.99</div>
                          <small class="upgrade-btn-period">per month</small>
                        </div>
                      </b-button>
                  </b-col>
                  <b-col cols="6">
                      <b-button class="upgrade-btn" variant="info" size="md" @click="upgradeYearly">
                        <div class="upgrade-btn-content">
                          <div class="upgrade-btn-title">Yearly membership</div>
                          <div class="upgrade-btn-price">£24.99</div>
                          <small class="upgrade-btn-period">per year</small>
                        </div>
                      </b-button>
                  </b-col>
              </b-row>
          </div>

        </b-modal>
    </div>
</template>

<script>
import { TRIAL_CONFIG } from '@/constants/trial';

export default {
    name: "UpgradeModal",
    props: {
        entryCount: {
            type: Number,
            default: 0
        },
        scheduledDeletionDate: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            currentTime: new Date(),
            countdownInterval: null
        }
    },
    computed: {
        getTrialStatus() {
            return this.$store.getters['authentication/getTrialStatus'];
        },
        isTrialUser() {
            return this.$store.getters['authentication/isTrialUser'];
        },
        getTrialEntriesRemaining() {
            return this.$store.getters['authentication/getTrialEntriesRemaining'];
        },
        trialStatus() {
            return this.getTrialStatus;
        },
        entriesRemaining() {
            return this.getTrialEntriesRemaining;
        },
        isTrialExpired() {
            return this.trialStatus.isTrialExpired;
        },
        modalTitle() {
            if (this.isTrialUser && !this.isTrialExpired) {
                return 'Your CPDme Trial';
            } else {
                return 'Account Locked - Upgrade Required';
            }
        },
        trialEndDate() {
            return this.formatDate(this.trialStatus.trialEndDate);
        },
        deletionDateFormatted() {
            const date = this.$store.getters['authentication/getScheduledDeletionDate'];
            return this.formatDate(date);
        },
        trialCountdownText() {
            if (!this.trialStatus.trialEndDate) return '';
            const trialEndDate = new Date(this.trialStatus.trialEndDate);
            return this.calculateCountdown(trialEndDate);
        },
        deletionCountdownText() {
            const date = this.$store.getters['authentication/getScheduledDeletionDate'];
            const deletionDate = new Date(date);
            return this.calculateCountdown(deletionDate);
        },
    },
        mounted() {
            // Update countdown every minute
            this.countdownInterval = setInterval(() => {
                this.currentTime = new Date();
            }, 60000);
        },
        beforeDestroy() {
            if (this.countdownInterval) {
                clearInterval(this.countdownInterval);
            }
        },
        methods: {
            show() {
                this.$refs.upgradeModal.show();
            },
            close() {
                this.$refs.upgradeModal.hide();
            },
            upgradeYearly() {
                window.open('https://pay.gocardless.com/AL0009FCR4M3J0', '_blank');
            },
            upgradeMonthly() {
                window.open('https://pay.gocardless.com/AL0009FCS98227', '_blank');
            },
            formatDate(dateString) {
                if (!dateString) return '';
                return new Date(dateString).toLocaleDateString();
            },
            calculateCountdown(targetDate) {
                const timeDiff = targetDate - this.currentTime;
                
                if (timeDiff <= 0) {
                    return 'Time expired';
                }
                
                const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
                const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
                
                if (days > 0) {
                    return `${days} days, ${hours} hours`;
                } else if (hours > 0) {
                    return `${hours} hours, ${minutes} minutes`;
                } else {
                    return `${minutes} minutes`;
                }
            }
        }
    }
</script>

<style scoped>
.upgrade-footer {
    width: 100%;
    text-align: center;
    padding: 0;
    margin: 0;
}

.upgrade-buttons {
    width: 100%;
    margin: 0;
}

.upgrade-btn {
    width: 100%;
    padding: 15px 10px;
    height: auto;
    min-height: 80px;
}

.upgrade-btn-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.upgrade-btn-title {
    font-weight: 600;
    font-size: 0.9rem;
}

.upgrade-btn-price {
    font-weight: bold;
    font-size: 1.1rem;
}

.upgrade-btn-period {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.8rem;
}

/* Usage Container Styling */
.usage-container {
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    padding: 15px;
    margin: 15px 0;
    text-align: center;
}

.usage-label {
    font-size: 0.9rem;
    font-weight: bold;
    color: #ffffff;
    margin-bottom: 5px;
}

.usage-display {
    font-size: 1.8rem;
    font-weight: bold;
    color: #ffffff;
    margin: 10px 0;
}

.usage-remaining {
    font-size: 0.9rem;
    color: #90EE90;
    margin-top: 5px;
}

.usage-warning {
    font-size: 0.9rem;
    font-weight: bold;
    color: #ffc107;
    margin-top: 5px;
}

/* Countdown Container Styling */
.countdown-container {
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    padding: 15px;
    margin: 15px 0;
    text-align: center;
}

.countdown-label {
    font-size: 0.9rem;
    font-weight: bold;
    color: #ffffff;
    margin-bottom: 5px;
}

.countdown-display {
    font-size: 1.8rem;
    font-weight: bold;
    color: #ffffff;
    margin: 10px 0;
}

.countdown-info {
    font-size: 0.8rem;
    color: #ffffff;
    margin-top: 5px;
}

.countdown-warning {
    font-size: 0.8rem;
    font-weight: bold;
    color: #ffc107;
    margin-top: 5px;
}

.entry-warning {
    margin: 15px 0;
}
</style>
